class CacheService {
  constructor() {
    /** @type {Map<string, {data: any, timestamp: number, expiresIn: number}>} */
    this.memoryCache = new Map();
    this.defaultExpiry = 5 * 60 * 1000; // 5 minutes

    this.loadFromStorage();
    window.addEventListener('beforeunload', () => this.saveToStorage());
  }

  /**
   * @param {string} endpoint
   * @param {Object} [params]
   * @returns {string}
   */
  generateKey(endpoint, params) {
    return `${endpoint}${params ? '_' + JSON.stringify(params) : ''}`;
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem('api_cache');
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([key, value]) => {
          this.memoryCache.set(key, value);
        });
      }
    } catch (error) {
      console.error('Failed to load cache from storage:', error);
    }
  }

  saveToStorage() {
    try {
      const cacheObject = Object.fromEntries(this.memoryCache.entries());
      localStorage.setItem('api_cache', JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Failed to save cache to storage:', error);
    }
  }

  /**
   * @template T
   * @param {string} endpoint
   * @param {Object} [params]
   * @returns {T|null}
   */
  get(endpoint, params) {
    const key = this.generateKey(endpoint, params);
    const entry = this.memoryCache.get(key);

    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.expiresIn;
    if (isExpired) {
      this.memoryCache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * @template T
   * @param {string} endpoint
   * @param {T} data
   * @param {Object} [params]
   * @param {number} [expiresIn]
   */
  set(endpoint, data, params, expiresIn = this.defaultExpiry) {
    const key = this.generateKey(endpoint, params);
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn,
    });
  }

  /**
   * @param {string} [endpoint]
   * @param {Object} [params]
   */
  clear(endpoint, params) {
    if (endpoint) {
      const key = this.generateKey(endpoint, params);
      this.memoryCache.delete(key);
    } else {
      this.memoryCache.clear();
    }
  }

  clearExpired() {
    const now = Date.now();
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now - entry.timestamp > entry.expiresIn) {
        this.memoryCache.delete(key);
      }
    }
  }
}

export const cacheService = new CacheService();
